import type { Dispatch, SetStateAction } from 'react'
import { formatFacetOptionLabel } from './productFilters'
import type { NumericFacet, NumericFilterState, OptionFacet, OptionFilterState } from './types'

type SearchNumericFacetSectionProps = {
  facet: NumericFacet
  numericFilters: NumericFilterState
  setNumericFilters: Dispatch<SetStateAction<NumericFilterState>>
}

export const SearchNumericFacetSection = ({ facet, numericFilters, setNumericFilters }: SearchNumericFacetSectionProps) => {
  const current = numericFilters[facet.id] ?? { min: '', max: '' }

  return (
    <section className="border-b border-slate-200 py-4 last:border-b-0">
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{facet.label}</p>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          inputMode="decimal"
          value={current.min}
          onChange={(event) =>
            setNumericFilters((state) => ({
              ...state,
              [facet.id]: { ...current, min: event.target.value },
            }))
          }
          placeholder={`${facet.min.toFixed(0)}`}
          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
        />
        <input
          type="number"
          inputMode="decimal"
          value={current.max}
          onChange={(event) =>
            setNumericFilters((state) => ({
              ...state,
              [facet.id]: { ...current, max: event.target.value },
            }))
          }
          placeholder={`${facet.max.toFixed(0)}`}
          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
        />
      </div>
    </section>
  )
}

type SearchOptionFacetSectionProps = {
  facet: OptionFacet
  language: 'it' | 'en'
  optionFilters: OptionFilterState
  setOptionFilters: Dispatch<SetStateAction<OptionFilterState>>
}

export const SearchOptionFacetSection = ({
  facet,
  language,
  optionFilters,
  setOptionFilters,
}: SearchOptionFacetSectionProps) => {
  const selected = optionFilters[facet.id] ?? []

  return (
    <section className="border-b border-slate-200 py-4 last:border-b-0">
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{facet.label}</p>
      <div className="space-y-1.5">
        {facet.options.map((value) => (
          <label key={value} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={selected.includes(value)}
              className="accent-cyan-700"
              onChange={(event) =>
                setOptionFilters((state) => {
                  const next = new Set(state[facet.id] ?? [])
                  if (event.target.checked) next.add(value)
                  else next.delete(value)
                  return { ...state, [facet.id]: Array.from(next) }
                })
              }
            />
            <span>
              {formatFacetOptionLabel(value, facet.key, language)}
              {typeof facet.counts?.[value] === 'number' ? ` (${facet.counts[value]})` : null}
            </span>
          </label>
        ))}
      </div>
    </section>
  )
}
